import React, { useCallback, useMemo, useState } from 'react';
import {
  GitBranch, Layers, FileText, ChevronRight, ChevronLeft, Save, Plus,
  CheckCircle2, Award,
} from 'lucide-react';
import { ROOT_CAUSE_CATEGORY_LABELS } from '../../types/issue';
import type { RootCauseAnalysis, RootCauseCategory, FiveWhyEntry, IshikawaCause } from '../../types/issue';
import { hypothesisText } from '../../utils/causeDraft';

const CATEGORIES: RootCauseCategory[] = ['method', 'machine', 'material', 'manpower', 'environment', 'measurement'];

const CAT_META: Record<RootCauseCategory, { style: string; desc: string; example: string }> = {
  method: {
    style: 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40',
    desc: 'Procedimentos, instruções de trabalho, métodos de operação e fluxos de processo.',
    example: 'Ex: Procedimento de inspeção não seguido na linha 3.',
  },
  machine: {
    style: 'border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-900/40',
    desc: 'Equipamentos, ferramentas, manutenção e condições operacionais de máquinas.',
    example: 'Ex: Calibração do equipamento vencida há 45 dias.',
  },
  material: {
    style: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40',
    desc: 'Matérias-primas, insumos, especificações e qualidade de materiais.',
    example: 'Ex: Lote de matéria-prima fora da especificação.',
  },
  manpower: {
    style: 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/40',
    desc: 'Competências, treinamento, alocação de pessoas e fatores humanos.',
    example: 'Ex: Operador sem treinamento na nova máquina.',
  },
  environment: {
    style: 'border-cyan-300 bg-cyan-50 dark:border-cyan-800 dark:bg-cyan-950/40',
    desc: 'Condições ambientais, layout, temperatura, umidade e organização do local.',
    example: 'Ex: Área de armazenamento com umidade acima do limite.',
  },
  measurement: {
    style: 'border-purple-300 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/40',
    desc: 'Sistemas de medição, calibração, indicadores e métodos de verificação.',
    example: 'Ex: Instrumento de medição com erro sistemático.',
  },
};

const STEP_HELP: Record<number, { title: string; help: string; example: string }> = {
  1: {
    title: 'Definir o Problema',
    help: 'Descreva o desvio observado de forma objetiva: o que aconteceu, onde, quando e qual o impacto no negócio. Evite soluções prematuras.',
    example: 'Ex: "Taxa de retrabalho na linha de montagem subiu de 2% para 8% nas últimas 2 semanas, gerando atraso de 120 unidades/dia."',
  },
  2: {
    title: 'Escolher Método de Análise',
    help: 'Selecione a ferramenta mais adequada. Use 5 Porquês para causas encadeadas simples; Ishikawa (6M) para problemas multifatoriais.',
    example: 'Ex: Para falha recorrente em equipamento → Ishikawa. Para desvio pontual de processo → 5 Porquês.',
  },
  3: {
    title: 'Conduzir a Investigação',
    help: 'Aprofunde a análise até identificar a causa fundamental — não pare no sintoma. Cada nível deve levar a uma causa mais profunda.',
    example: 'Ex: "Peça com defeito" → "Solda irregular" → "Temperatura baixa" → "Termostato descalibrado" → "Manutenção preventiva atrasada".',
  },
  4: {
    title: 'Hipótese de causa',
    help: 'O texto abaixo ainda é hipótese. Só o botão de confirmar transforma essa explicação em causa. O último porquê e um item do Ishikawa não fazem isso sozinhos.',
    example: 'Ex: "Ausência de calibração preventiva do termostato", com a justificativa do que sustenta essa conclusão.',
  },
  5: {
    title: 'Classificar por Categoria 6M',
    help: 'Identifique a categoria principal (Método, Máquina, Material, Mão de Obra, Meio Ambiente ou Medição) para direcionar ações corretivas.',
    example: 'Ex: Problema de calibração → categoria "Medição" ou "Máquina".',
  },
  6: {
    title: 'Plano de Ação Corretiva',
    help: 'Salve a análise e crie ações corretivas vinculadas ao problema para tratar a causa raiz identificada.',
    example: 'Ex: Ação 1: Implementar calibração mensal. Ação 2: Treinar equipe de manutenção.',
  },
  7: {
    title: 'Análise Concluída',
    help: 'A investigação foi registrada oficialmente. O caso está pronto para execução do plano de ação.',
    example: '',
  },
};

type MethodChoice = 'five_whys' | 'ishikawa' | 'simple' | null;

function empty(): RootCauseAnalysis {
  return {
    problem_statement: '',
    root_cause_description: '',
    root_cause_category: 'method',
    five_whys: [{ level: 1, question: 'Por quê 1', answer: '' }],
    ishikawa: CATEGORIES.map(c => ({ category: c, causes: [] })),
  };
}

interface RootCauseWizardProps {
  value: RootCauseAnalysis | null;
  onSave: (analysis: RootCauseAnalysis) => void;
  onCreateActions: () => void;
  saving?: boolean;
}

export const RootCauseWizard: React.FC<RootCauseWizardProps> = ({
  value,
  onSave,
  onCreateActions,
  saving = false,
}) => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<RootCauseAnalysis>(() => value ?? empty());
  const [method, setMethod] = useState<MethodChoice>(() => {
    if (value?.five_whys?.some(w => w.answer.trim())) return 'five_whys';
    if (value?.ishikawa?.some(g => g.causes.length)) return 'ishikawa';
    if (value?.root_cause_description?.trim()) return 'simple';
    return null;
  });
  const [saved, setSaved] = useState(false);

  const problemLen = data.problem_statement.length;
  const problemValid = problemLen >= 20;
  const rootValid = data.root_cause_description.trim().length >= 10;
  const reviewing = Boolean(value?.problem_statement?.trim() || value?.root_cause_description?.trim());

  const patch = useCallback((p: Partial<RootCauseAnalysis>) => setData(prev => ({ ...prev, ...p })), []);

  const ensureFiveWhys = useCallback(() => {
    const whys = [...(data.five_whys ?? [])];
    while (whys.length < 5) {
      whys.push({ level: whys.length + 1, question: `Por quê ${whys.length + 1}`, answer: '' });
    }
    patch({ five_whys: whys });
  }, [data.five_whys, patch]);

  const totalSteps = 7;
  const progressPct = Math.round((step / totalSteps) * 100);

  const steps = useMemo(() => [
    { n: 1, label: 'Problema' },
    { n: 2, label: 'Método' },
    { n: 3, label: 'Investigação' },
    { n: 4, label: 'Conclusão' },
    { n: 5, label: 'Categoria' },
    { n: 6, label: 'Ações' },
    { n: 7, label: 'Finalizado' },
  ], []);

  const whysFilled = (data.five_whys ?? []).filter(w => w.answer.trim()).length;
  const ishikawaFilled = (data.ishikawa ?? []).reduce((s, g) => s + g.causes.length, 0);

  const inputCls = 'w-full rounded border border-slate-200 bg-white px-3 py-2 text-[13px] leading-relaxed text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';

  const handleSave = () => {
    onSave(data);
    setSaved(true);
    setStep(7);
  };

  const help = STEP_HELP[step];

  return (
    <div className="mx-auto max-w-4xl">
      {/* Wizard header with progress */}
      <div className="mb-4 rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 dark:border-slate-800">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Assistente de Causa Raiz</p>
            <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">Etapa {step} de {totalSteps} — {steps[step - 1]?.label}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-4 py-2">
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex gap-0 overflow-x-auto border-t border-slate-100 px-2 py-2 dark:border-slate-800">
          {steps.map(s => (
            <button
              key={s.n}
              type="button"
              onClick={() => { if (reviewing || s.n <= step || s.n === 1) { if (s.n === 3 && method) ensureFiveWhys(); setStep(s.n); } }}
              className={`flex shrink-0 flex-col items-center px-2 py-1 ${
                step === s.n ? 'opacity-100' : step > s.n ? 'opacity-70' : 'opacity-40'
              }`}
            >
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
                step > s.n ? 'bg-emerald-600 text-white' : step === s.n ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500 dark:bg-slate-700'
              }`}>
                {step > s.n ? '✓' : s.n}
              </span>
              <span className={`mt-0.5 text-[11px] ${step === s.n ? 'font-bold text-blue-600' : 'text-slate-500'}`}>{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="rounded border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
          {help && step < 7 && (
            <div className="mb-4 rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
              <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-100">{help.title}</p>
              <p className="mt-1 text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">{help.help}</p>
              {help.example && (
                <p className="mt-2 text-[12px] leading-relaxed text-slate-500">{help.example}</p>
              )}
            </div>
          )}
          {step === 1 && (
            <div className="space-y-3">
              <h3 className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">Definir o Problema Observado</h3>
              <textarea
                value={data.problem_statement}
                onChange={e => patch({ problem_statement: e.target.value })}
                rows={8}
                className={inputCls}
                placeholder="O que foi observado, onde, quando e qual impacto..."
              />
              <div className="flex justify-between text-[11px]">
                <span className={problemValid ? 'font-medium text-emerald-600' : 'text-amber-600'}>{problemLen} / 20 caracteres mínimos</span>
                <span className={problemValid ? 'font-semibold text-emerald-600' : 'text-slate-500'}>
                  {problemValid ? 'Pronto para avançar' : reviewing ? 'Texto já registrado — você pode revisar as próximas etapas' : 'Complete o texto para avançar'}
                </span>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <h3 className="text-[14px] font-semibold">Escolher Método de Investigação</h3>
              <div className="grid gap-3 sm:grid-cols-3">
                {([
                  { id: 'five_whys' as const, icon: GitBranch, title: '5 Porquês', desc: 'Investigação encadeada até a causa fundamental.', best: 'Causas lineares' },
                  { id: 'ishikawa' as const, icon: Layers, title: 'Ishikawa (6M)', desc: 'Mapeamento de causas por categoria.', best: 'Problemas multifatoriais' },
                  { id: 'simple' as const, icon: FileText, title: 'Análise Direta', desc: 'Registro direto sem ferramenta estruturada.', best: 'Casos simples' },
                ]).map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setMethod(m.id); if (m.id !== 'simple') ensureFiveWhys(); }}
                    className={`rounded-lg border-2 p-4 text-left transition-colors ${
                      method === m.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                        : 'border-slate-200 hover:border-slate-300 dark:border-slate-700'
                    }`}
                  >
                    <m.icon size={20} className="mb-2 text-blue-600" />
                    <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100">{m.title}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{m.desc}</p>
                    <p className="mt-2 text-[10px] font-medium text-blue-600">Ideal para: {m.best}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && method === 'five_whys' && (
            <div className="space-y-3">
              <h3 className="text-[14px] font-semibold">Investigação — 5 Porquês</h3>
              <p className="text-[12px] text-slate-500">Responda cada nível. A resposta de cima vira a pergunta do próximo.</p>

              {(data.five_whys ?? []).slice(0, 5).map((why, idx) => (
                <div key={idx}>
                  <label className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 dark:text-slate-200">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[11px] text-white">{idx + 1}</span>
                    Por quê {idx + 1}?
                  </label>
                  <textarea
                    value={why.answer}
                    onChange={e => {
                      const whys = [...(data.five_whys ?? [])];
                      whys[idx] = { ...whys[idx], answer: e.target.value };
                      patch({ five_whys: whys });
                    }}
                    rows={2}
                    className={`${inputCls} mt-1.5`}
                    placeholder={idx === 0 ? 'Por que o problema ocorreu?' : `Por que "${(data.five_whys ?? [])[idx - 1]?.answer.slice(0, 60) || '…'}"?`}
                  />
                </div>
              ))}
              <p className="text-[12px] text-slate-500">{whysFilled} de 5 níveis preenchidos</p>
              {whysFilled > 0 && (
                <p className="text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
                  <span className="font-medium text-slate-500">Cadeia: </span>
                  {[data.problem_statement, ...(data.five_whys ?? []).map(entry => entry.answer).filter(answer => answer.trim())]
                    .filter(Boolean)
                    .join(' → ')}
                </p>
              )}
            </div>
          )}

          {step === 3 && method === 'ishikawa' && (
            <div className="space-y-3">
              <h3 className="text-[14px] font-semibold">Investigação — Diagrama Ishikawa (6M)</h3>
              <p className="text-[11px] text-slate-500">Organize hipóteses por categoria. Um item preenchido não é causa confirmada. {ishikawaFilled} hipótese{ishikawaFilled !== 1 ? 's' : ''}.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {(data.ishikawa ?? []).map((group, catIdx) => (
                  <IshikawaBlock
                    key={group.category}
                    group={group}
                    onAdd={cause => {
                      const ish = [...(data.ishikawa ?? [])];
                      ish[catIdx] = { ...ish[catIdx], causes: [...ish[catIdx].causes, cause] };
                      patch({ ishikawa: ish });
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 3 && method === 'simple' && (
            <div className="rounded border border-slate-200 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-900/50">
              <FileText size={28} className="mx-auto text-slate-400" />
              <p className="mt-2 text-[13px] font-medium text-slate-700 dark:text-slate-200">Análise direta selecionada</p>
              <p className="mt-1 text-[11px] text-slate-500">Avance para registrar a conclusão oficial da causa raiz.</p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Award size={18} className="text-emerald-600" />
                <h3 className="text-[14px] font-semibold">Hipótese de causa</h3>
              </div>
              <div className="rounded-lg border-2 border-emerald-400 bg-emerald-50/50 p-4 dark:border-emerald-700 dark:bg-emerald-950/20">
                <p className="mb-2 text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-400">Explicação proposta. Ainda não é causa confirmada.</p>
                <textarea
                  value={data.root_cause_description}
                  onChange={e => patch({ root_cause_description: e.target.value, cause_confirmed: false })}
                  rows={4}
                  className={`${inputCls} border-emerald-300 bg-white`}
                  placeholder="Descreva a explicação que pode ser confirmada como causa..."
                />
                <textarea
                  value={data.cause_evidence ?? ''}
                  onChange={e => patch({ cause_evidence: e.target.value, cause_confirmed: false })}
                  rows={2}
                  className={`${inputCls} mt-2`}
                  placeholder="Justificativa ou evidência que sustenta essa explicação"
                />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={!rootValid || !(data.cause_evidence ?? '').trim()}
                    onClick={() => patch({
                      cause_confirmed: true,
                      analysis_method: method === 'five_whys' ? 'five_whys' : method === 'ishikawa' ? 'ishikawa' : method === 'simple' ? 'free' : 'other',
                    })}
                    className="rounded bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Confirmar como causa
                  </button>
                  <p className={`text-[11px] ${data.cause_confirmed ? 'font-medium text-emerald-700' : 'text-amber-700'}`}>
                    {data.cause_confirmed ? 'Causa confirmada por decisão humana.' : 'Sem confirmação, isto continua hipótese.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3">
              <h3 className="text-[14px] font-semibold">Categoria Principal (6M)</h3>
              <p className="text-[11px] text-slate-500">Selecione a categoria que melhor classifica a causa raiz confirmada.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {CATEGORIES.map(cat => {
                  const meta = CAT_META[cat];
                  const selected = data.root_cause_category === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => patch({ root_cause_category: cat })}
                      className={`rounded-lg border-2 p-3 text-left transition-colors ${
                        selected ? `${meta.style} ring-2 ring-blue-400 ring-offset-1` : 'border-slate-200 hover:border-slate-300 dark:border-slate-700'
                      }`}
                    >
                      <p className="text-[12px] font-bold text-slate-800 dark:text-slate-100">{ROOT_CAUSE_CATEGORY_LABELS[cat]}</p>
                      <p className="mt-1 text-[10px] leading-relaxed text-slate-500">{meta.desc}</p>
                      <p className="mt-1.5 text-[10px] italic text-slate-400">{meta.example}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4 text-center">
              <h3 className="text-[14px] font-semibold">Gerar Plano de Ação Corretiva</h3>
              <p className="text-[12px] text-slate-500">Salve a análise oficial e crie ações corretivas vinculadas ao problema.</p>

              <div className="rounded border border-slate-200 bg-slate-50 p-4 text-left dark:border-slate-700 dark:bg-slate-900/50">
                <p className="text-[10px] font-bold uppercase text-slate-400">Resumo da análise</p>
                <p className="mt-2 text-[12px] text-slate-700 dark:text-slate-300">{data.root_cause_description || '—'}</p>
                <p className="mt-2 text-[11px] text-slate-500">
                  Categoria: <strong>{ROOT_CAUSE_CATEGORY_LABELS[data.root_cause_category]}</strong>
                  {method === 'five_whys' && ` · ${whysFilled} níveis 5 Porquês`}
                  {method === 'ishikawa' && ` · ${ishikawaFilled} causas Ishikawa`}
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-2">
                <button type="button" disabled={saving || !rootValid} onClick={handleSave} className="flex items-center gap-1.5 rounded bg-emerald-600 px-5 py-2 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                  <Save size={14} /> Salvar Análise Oficial
                </button>
                <button type="button" onClick={onCreateActions} className="flex items-center gap-1.5 rounded bg-blue-600 px-5 py-2 text-[12px] font-semibold text-white hover:bg-blue-700">
                  <Plus size={14} /> Criar Ações Corretivas
                </button>
              </div>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-4 py-6 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h3 className="text-[16px] font-bold text-slate-800 dark:text-slate-100">Análise de Causa Raiz Concluída</h3>
              <p className="mx-auto max-w-md text-[12px] text-slate-500">
                {saved
                  ? 'A investigação foi registrada oficialmente. O caso está pronto para execução do plano de ação corretiva.'
                  : 'Revise os dados e salve a análise para concluir oficialmente a investigação.'}
              </p>
              {data.root_cause_description && (
                <div className="mx-auto max-w-lg rounded border border-emerald-200 bg-emerald-50/50 p-4 text-left dark:border-emerald-800 dark:bg-emerald-950/20">
                  <p className="text-[10px] font-bold uppercase text-emerald-700">Causa raiz confirmada</p>
                  <p className="mt-1 text-[12px] font-medium text-slate-800 dark:text-slate-200">{data.root_cause_description}</p>
                </div>
              )}
              {!saved && (
                <button type="button" disabled={saving} onClick={handleSave} className="mx-auto flex items-center gap-1.5 rounded bg-emerald-600 px-5 py-2 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                  <Save size={14} /> Salvar e Concluir
                </button>
              )}
            </div>
          )}

          {/* Navigation */}
          {step < 7 && (
            <div className="mt-5 flex justify-between border-t border-slate-200 pt-4 dark:border-slate-700">
              <button type="button" disabled={step <= 1} onClick={() => setStep(s => s - 1)} className="flex items-center gap-1 rounded border border-slate-200 px-4 py-1.5 text-[11px] font-medium disabled:opacity-40 dark:border-slate-600">
                <ChevronLeft size={14} /> Anterior
              </button>
              {step < 6 && (
                <button
                  type="button"
                  disabled={(step === 1 && !problemValid && !reviewing) || (step === 2 && !method)}
                  onClick={() => { if (step === 2 && method) ensureFiveWhys(); setStep(s => s + 1); }}
                  className="flex items-center gap-1 rounded bg-blue-600 px-4 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40"
                >
                  Próximo <ChevronRight size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function IshikawaBlock({ group, onAdd }: { group: IshikawaCause; onAdd: (c: string) => void }) {
  const [txt, setTxt] = useState('');
  const meta = CAT_META[group.category];
  return (
    <div className={`rounded-lg border p-3 ${meta.style}`}>
      <p className="text-[11px] font-bold text-slate-800 dark:text-slate-100">{ROOT_CAUSE_CATEGORY_LABELS[group.category]}</p>
      <p className="mt-0.5 text-[9px] text-slate-500">{meta.desc}</p>
      <ul className="mt-2 space-y-0.5">
        {group.causes.map((cause, index) => <li key={index} className="text-[10px] text-slate-700 dark:text-slate-300">• {hypothesisText(cause)}</li>)}
      </ul>
      <div className="mt-2 flex gap-1">
        <input
          value={txt}
          onChange={e => setTxt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && txt.trim()) { onAdd(txt.trim()); setTxt(''); } }}
          className="flex-1 rounded border border-slate-200 px-2 py-1 text-[10px] dark:border-slate-600 dark:bg-slate-800"
          placeholder="Nova hipótese..."
        />
        <button type="button" onClick={() => { if (txt.trim()) { onAdd(txt.trim()); setTxt(''); } }} className="rounded bg-white/80 px-2.5 text-[10px] font-bold dark:bg-slate-800">+</button>
      </div>
    </div>
  );
}

export default RootCauseWizard;
